(ns dashboard.components.select
  (:require
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]
   [headlessui-reagent.core :as ui]
   [dashboard.utils :refer [cs listen format-week format-day]]
   [dashboard.i18n :refer [tr]]
   [dashboard.components.tag :refer [tag]]
   [dashboard.components.profile :refer [profile-img]]
   [dashboard.subs :as subs]
   ["@heroicons/react/solid" :refer
    [SelectorIcon LocationMarkerIcon ChevronLeftIcon ChevronRightIcon]]
   ["date-fns" :refer
    [addMinutes parse format isBefore isAfter addMonths subMonths addWeeks
     subWeeks addDays subDays]]))

(defn select
  [{:keys [selected items-list label on-change class small? disabled? tag?
           id-key name-key color-key intent-key]
    :or {items-list []
         on-change identity
         small? false
         disabled? false
         tag? false
         id-key :id
         name-key :name
         color-key :color
         intent-key :intent}}]
  [:<>
   [ui/listbox
    {:value (:key selected)
     :on-change on-change
     :disabled disabled?}
    (fn [{:keys [open]}]
      [:<>
       (when (some? label)
         [ui/listbox-label
          {:class (cs "block text-sm font-medium text-gray-700"
                      (if disabled? "text-gray-300" "text-gray-700"))}
          label])
       [:div
        {:class class}
        [ui/listbox-button
         {:class (cs "relative w-full border rounded-md shadow-sm pl-3 pr-10 py-text-left cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     (when (some? class) class)
                     (if disabled? "text-gray-500 bg-gray-50 border-gray-200" "text-black border-gray-300 bg-white")
                     (if small? "py-1" "py-2"))
          :disabled disabled?}
         [:span.flex.items-center.h-5
          (when (some? (:icon selected))
            (:icon selected))
          (if tag?
            (when (some? selected)
              [tag
               {:color (color-key selected)
                :intent (intent-key selected)}
               (name-key selected)])
            [:span.block.truncate
             (name-key selected)])]
         [:span.absolute.inset-y-0.right-0.flex.items-center.pr-2.pointer-events-none
          [:> SelectorIcon
           {:class (cs "h-5 w-5 text-gray-400"
                       (when open "transform rotate-180"))
            :aria-hidden true}]]]
        [ui/transition
         {:show open
          :class "relative z-40"
          :leave "transition ease-in duration-100"
          :leave-from "opacity-100"
          :leave-to "opacity-0"}
         [ui/listbox-options
          {:class "absolute z-40 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"}
          (doall
           (for [item items-list]
             ^{:key (str "select-" (id-key item))}
             [ui/listbox-option
              {:value (id-key item)
               :class (fn [{:keys [active selected]}]
                        (cs "cursor-default select-none relative py-2 pl-3 pr-9"
                            (if tag?
                              (if selected "bg-gray-50"
                                  (if active "bg-primary-50" "bg-white"))
                              (if selected "text-white bg-primary-600"
                                  (if active "text-primary-800 bg-primary-50" "text-gray-900")))))}
              (fn [{:keys [active selected]}]
                [:span.flex.items-center
                 (when (some? (:icon item))
                   (:icon item))
                 (if tag?
                   [tag
                    {:color (color-key item)
                     :intent (intent-key item)}
                    (name-key item)]
                   [:span.whitespace-nowrap
                    (name-key item)])])]))]]]])]])

(defn multiselect
  [{:keys [selected items-list label on-change class small? disabled? id-key name-key]
    :or {items-list []
         on-change identity
         small? false
         disabled? false
         id-key :id
         name-key :name}}]
  [:<>
   [ui/listbox
    {:value selected
     :on-change on-change
     :disabled disabled?}
    (fn [{:keys [open]}]
      [:<>
       (when (some? label)
         [ui/listbox-label
          {:class (cs "block text-sm font-medium text-gray-700"
                      (if disabled? "text-gray-300" "text-gray-700"))}
          label])
       [:div
        {:class class}
        [ui/listbox-button
         {:class (cs "relative w-full border rounded-md shadow-sm pl-3 pr-10 py-text-left cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     (when (some? class) class)
                     (if disabled? "text-gray-500 bg-gray-50 border-gray-200" "text-black border-gray-300 bg-white")
                     (if small? "py-1" "py-2"))
          :disabled disabled?}
         (if (not (empty? selected))
           [:div.flex.space-x-1
            (doall
             (for [value selected]
               ^{:key (str "mutliselect-" (id-key value))}
               [:span.inline-items-center.rounded-full.text-xs.font-medium.text-white
                {:class (cs "px-2.5 py-0.5"
                            (str "bg-" (or (:color value) "primary") "-500"))}
                (when (some? (:icon value))
                  (:icon value))
                [:span.truncate
                 (name-key value)]]))]
           [:div.flex.space-x-1
            [:span.inline-items-center
             {:class (cs "px-2.5 py-0.5")}
             [:span.block.truncate.h-4]]])
         [:span.absolute.inset-y-0.right-0.flex.items-center.pr-2.pointer-events-none
          [:> SelectorIcon
           {:class (cs "h-5 w-5 text-gray-400"
                       (when open "transform rotate-180"))
            :aria-hidden true}]]]
        [ui/transition
         {:show open
          :class "relative z-40"
          :leave "transition ease-in duration-100"
          :leave-from "opacity-100"
          :leave-to "opacity-0"}
         [ui/listbox-options
          {:class "absolute z-40 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"}
          (doall
           (for [item items-list
                 :let [selected? (some? ((into #{} (map id-key selected)) (id-key item)))]]
             ^{:key (str "select-" (id-key item))}
             [ui/listbox-option
              {:value (id-key item)
               :class  (fn [{:keys [active selected]}]
                         (cs "cursor-default select-none relative py-2 pl-3 pr-9"
                             ;; TODO: we would idealy want to use selected instead of selected?
                             (if selected? "text-white bg-primary-600"
                                 (if active "bg-gray-50 text-primary-800" "bg-white text-gray-900"))))}
              (fn [{:keys [active selected]}]
                [:span.inline-items-center.rounded-full.text-xs.font-medium.text-white
                 {:class (cs "px-2.5 py-0.5"
                             (when (not selected)
                               (str "bg-" (or (:color item) "primary") "-500")))}
                 (when (some? (:icon item))
                   (:icon item))
                 [:span.whitespace-nowrap
                  (name-key item)]])]))]]]])]])

(defn cell-select
  [{:keys [selected items-list on-change class small? disabled? id-key name-key]
    :or {items-list []
         on-change identity
         small? false
         id-key :id
         name-key :name}}]
  [:div
   [ui/listbox
    {:value (:key selected)
     :on-change on-change
     :disabled disabled?}
    (fn [{:keys [open]}]
      [:<>
       [:div.relative.w-full.h-full
        [ui/listbox-button
         {:class (cs "w-full relative cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     (when (some? class) class))
          :disabled disabled?}
         [:span
          (when (some? (:icon selected))
            (:icon selected))
          [:span.block.truncate
           (name-key selected)]]]
        [ui/transition
         {:show open
          :leave "transition ease-in duration-100"
          :leave-from "opacity-100"
          :leave-to "opacity-0"}
         [ui/listbox-options
          {:class "absolute z-30 mt-2 w-full bg-white shadow-lg max-h-90 py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"}
          (doall
           (for [item items-list
                 :let [active? (= (id-key item) (id-key selected))]]
             ^{:key (str "select-" (id-key item))}
             [ui/listbox-option
              {:value (id-key item)
               :class (fn [{:keys [active selected]}]
                        (cs "cursor-default select-none relative py-2 px-1"
                            (if active? "text-white bg-primary-600" "text-gray-900")))}
              [:span.flex.items-center
               (when (some? (:icon item))
                 (:icon item))
               [:span.whitespace-nowrap.flex.items-center.justify-center
                (name-key item)]]]))]]]])]])

(defn month-select
  [{:keys [month start-month end-month on-change class disabled?]
    :or {on-change identity
         disabled? false}}]
  [:div.flex.font-semibold
   [:button.-m-1.5.flex.flex-none.items-center.justify-center.p-1.5.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-primary-500.rounded
    {:type "button"
     :on-click #(reset! month (subMonths @month 1))}
    [:span.sr-only
     (tr :time/previous-month)]
    [:> ChevronLeftIcon
     {:class "h-5 w-5"
      :aria-hidden true}]]
   [:div.sm:text-lg.font-semibold.mx-4.sm:w-28.flex.justify-center.items-center
    [:time
     {:dateTime (format @month "yyyy-MM")}
     (if (= (listen ::subs/locale) :ja)
       [:span.flex.space-x-1
        [:span.sr-only.sm:not-sr-only.text-gray-400
         (str (format @month "yyyy" #js {:locale (listen ::subs/date-fns-locale)}) "年")]
        [:span
         (format @month "MMMM" #js {:locale (listen ::subs/date-fns-locale)})]]
       [:span.flex.space-x-1
        [:span
         (format @month "MMMM" #js {:locale (listen ::subs/date-fns-locale)})]
        [:span.sr-only.sm:not-sr-only.text-gray-400
         (format @month "yyyy" #js {:locale (listen ::subs/date-fns-locale)})]])]]
   [:button.-m-1.5.flex.flex-none.items-center.justify-center.p-1.5.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-primary-500.rounded
    {:type "button"
     :disabled (when (some? end-month)
                 (isAfter @month end-month))
     :on-click #(reset! month (addMonths @month 1))}
    [:span.sr-only
     (tr :time/next-month)]
    [:> ChevronRightIcon
     {:class "h-5 w-5"
      :aria-hidden true}]]])

(defn week-select
  [{:keys [week on-change class disabled?]
    :or {on-change identity
         disabled? false}}]
  [:div.flex.font-semibold
   [:button.-m-1.5.flex.flex-none.items-center.justify-center.p-1.5.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-primary-500.rounded
    {:type "button"
     :on-click #(reset! week (subWeeks @week 1))}
    [:span.sr-only
     (tr :time/previous-week)]
    [:> ChevronLeftIcon
     {:class "h-5 w-5"
      :aria-hidden true}]]
   [:div.sm:text-lg.font-semibold.mx-4.w-16.sm:w-28.flex.justify-center.items-center
    [:time
     {:dateTime (format @week "yyyy-MM-dd")}
     (if (= (listen ::subs/locale) :ja)
       [:span.flex.space-x-1
        [:span.sr-only.sm:not-sr-only.text-gray-400
         (format-week @week)]
        [:span
         (format @week "MMMM do" #js {:locale (listen ::subs/date-fns-locale)})]]
       [:span.flex.space-x-1
        [:span
         (format @week "MMMM d" #js {:locale (listen ::subs/date-fns-locale)})]
        [:span.sr-only.sm:not-sr-only.text-gray-400
         (format-week @week)]])]]
   [:button.-m-1.5.flex.flex-none.items-center.justify-center.p-1.5.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-primary-500.rounded
    {:type "button"
     :on-click #(reset! week (addWeeks @week 1))}
    [:span.sr-only
     (tr :time/next-week)]
    [:> ChevronRightIcon
     {:class "h-5 w-5"
      :aria-hidden true}]]])

(defn day-select
  [{:keys [day on-change class disabled?]
    :or {on-change identity
         disabled? false}}]
  [:div.flex.font-semibold
   [:button.-m-1.5.flex.flex-none.items-center.justify-center.p-1.5.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-primary-500.rounded
    {:type "button"
     :on-click #(reset! day (subDays @day 1))}
    [:span.sr-only
     (tr :time/previous-day)]
    [:> ChevronLeftIcon
     {:class "h-5 w-5"
      :aria-hidden true}]]
   [:div.sm:text-lg.font-semibold.mx-4.w-16.sm:w-28.flex.justify-center.items-center.whitespace-nowrap
    [:time
     {:dateTime (format @day "yyyy-MM-dd")}
     (format @day "PPP" #js {:locale (listen ::subs/date-fns-locale)})]]
   [:button.-m-1.5.flex.flex-none.items-center.justify-center.p-1.5.text-gray-400.hover:text-gray-500.focus:outline-none.focus:ring-2.focus:ring-primary-500.rounded
    {:type "button"
     :on-click #(reset! day (addDays @day 1))}
    [:span.sr-only
     (tr :time/next-day)]
    [:> ChevronRightIcon
     {:class "h-5 w-5"
      :aria-hidden true}]]])

(defn time-select
  [{:keys [selected label on-change class disabled? start-time end-time step-minutes small?]
    :or {start-time "08:00"
         end-time "20:00"
         step-minutes 30
         on-change identity
         disabled? false
         small? false}}]
  (let [start-date (parse start-time "HH:mm" (js/Date.))
        end-date (parse end-time "HH:mm" (js/Date.))
        new-date (atom start-date)
        result (atom [])
        _ (while (isBefore @new-date end-date)
            (swap! result conj @new-date)
            (reset! new-date (addMinutes @new-date step-minutes)))
        items-list (->> @result
                        (map #(hash-map :id (format % "HH:mm")
                                        :name (format % "HH:mm"))))]
    [:<>
     [ui/listbox
      {:value (:key selected)
       :on-change on-change
       :disabled disabled?}
      (fn [{:keys [open]}]
        [:<>
         (when (some? label)
           [ui/listbox-label
            {:class (cs "block text-sm font-medium text-gray-700"
                        (if disabled? "text-gray-300" "text-gray-700"))}
            label])
         [:div
          {:class class}
          [ui/listbox-button
           {:class (cs "bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-text-left cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                       (when (some? class) class)
                       (if disabled? "text-gray-300 bg-gray-50" "text-black")
                       (if small? "py-1" "py-2"))
            :disabled disabled?}
           [:span.flex.items-center.h-4
            [:span.block.truncate
             (:name selected)]]
           [:span.absolute.inset-y-0.right-0.flex.items-center.pr-2.pointer-events-none
            [:> SelectorIcon
             {:class (cs "h-5 w-5 text-gray-400"
                         (when open "transform rotate-180"))
              :aria-hidden true}]]]
          [ui/transition
           {:show open
            :class "relative z-40"
            :leave "transition ease-in duration-100"
            :leave-from "opacity-100"
            :leave-to "opacity-0"}
           [ui/listbox-options
            {:class "absolute z-40 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"}
            (doall
             (for [item items-list
                   :let [active? (= (:id item) (:id selected))]]
               ^{:key (str "select-" (:id item))}
               [ui/listbox-option
                {:value (:id item)
                 :class (fn [{:keys [active selected]}]
                          (cs "cursor-default select-none relative py-2 pl-3 pr-9"
                              (if active? "text-white bg-primary-600" "text-gray-900")))}
                [:span.flex.items-center
                 [:span.whitespace-nowrap
                  (:name item)]]]))]]]])]]))

;; -------------------------
;; Custom Select

(defn user-select
  [{:keys [selected items-list label on-change class small? disabled? id-key name-key img-key]
    :or {items-list []
         on-change identity
         small? false
         disabled? false
         id-key :id
         name-key :name
         img-key :img}}]
  [:<>
   [ui/listbox
    {:value (id-key selected)
     :on-change on-change
     :disabled disabled?}
    (fn [{:keys [open]}]
      [:<>
       (when (some? label)
         [ui/listbox-label
          {:class (cs "block text-sm font-medium text-gray-700"
                      (if disabled? "text-gray-300" "text-gray-700"))}
          label])
       [:div
        {:class class}
        [ui/listbox-button
         {:class (cs "bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-text-left cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     (when (some? class) class)
                     (if disabled? "text-gray-300" "text-black")
                     (if small? "py-1" "py-2"))
          :disabled disabled?}
         [:span.flex.items-center
          [profile-img
           {:class "flex-shrink-0 h-6 w-6 text-xs"
            :src (img-key selected)
            :text (if (some? (:lastNameJp selected))
                    (:lastNameJp selected)
                    (str (first (:firstName selected))
                         (first (:lastName selected))))}]
          [:span.ml-3.block.truncate
           (name-key selected)]]
         [:span.absolute.inset-y-0.right-0.flex.items-center.pr-2.pointer-events-none
          [:> SelectorIcon
           {:class (cs "h-5 w-5 text-gray-400"
                       (when open "transform rotate-180"))
            :aria-hidden true}]]]
        [ui/transition
         {:show open
          :class "relative z-40"
          :leave "transition ease-in duration-100"
          :leave-from "opacity-100"
          :leave-to "opacity-0"}
         [ui/listbox-options
          {:class "absolute z-40 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"}
          (doall
           (for [item items-list
                 :let [active? (= (id-key item) (id-key selected))]]
             ^{:key (str "select-" (id-key item))}
             [ui/listbox-option
              {:value (id-key item)
               :class (fn [{:keys [active selected]}]
                        (cs "cursor-default select-none relative py-2 pl-3 pr-9"
                            (if active? "text-white bg-primary-600" "text-gray-900")))}
              [:div.flex.items-center
               [profile-img
                {:class "h-6 w-6 text-xs"
                 :src (img-key item)
                 :text (if (some? (:last_name_jp item))
                         (:last_name_jp item)
                         (str (first (:firstName item))
                              (first (:lastName item))))}]
               [:span.ml-3.block.whitespace-nowrap.truncate
                (name-key item)]]]))]]]])]])

(defn space-select
  [{:keys [selected items-list label on-change class disabled? small? id-key]
    :or {items-list (listen ::subs/active-spaces)
         on-change identity
         disabled? false
         small? false
         id-key :id}}]
  [:<>
   [ui/listbox
    {:value (:key selected)
     :on-change on-change
     :disabled disabled?}
    (fn [{:keys [open]}]
      [:<>
       (when (some? label)
         [ui/listbox-label
          {:class (cs "block text-sm font-medium text-gray-700"
                      (if disabled? "text-gray-300" "text-gray-700"))}
          label])
       [:div
        {:class class}
        [ui/listbox-button
         {:class (cs "bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-text-left cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     (when (some? class) class)
                     (if disabled? "text-gray-300" "text-black")
                     (if small? "py-1" "py-2"))
          :disabled disabled?}
         [:span.flex.items-center.h-4
          [:> LocationMarkerIcon
           {:class "h-5 w-5 text-gray-400 mr-2"}]
          [:span.block.truncate
           (if (= (listen ::subs/locale) :ja)
             (:nameJp selected)
             (:name selected))]]
         [:span.absolute.inset-y-0.right-0.flex.items-center.pr-2.pointer-events-none
          [:> SelectorIcon
           {:class (cs "h-5 w-5 text-gray-400"
                       (when open "transform rotate-180"))
            :aria-hidden true}]]]
        [ui/transition
         {:show open
          :class "relative z-40"
          :leave "transition ease-in duration-100"
          :leave-from "opacity-100"
          :leave-to "opacity-0"}
         [ui/listbox-options
          {:class "absolute z-40 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"}
          (doall
           (for [item items-list
                 :let [active? (= (id-key item) (id-key selected))]]
             ^{:key (str "select-" (id-key item))}
             [ui/listbox-option
              {:value (id-key item)
               :class (fn [{:keys [active selected]}]
                        (cs "cursor-default select-none relative py-2 pl-3 pr-9"
                            (if active? "text-white bg-primary-600" "text-gray-900")))}
              [:span.flex.items-center
               [:> LocationMarkerIcon
                {:class (cs "h-5 w-5 mr-2"
                            (if active? "text-white" "text-gray-400" ))}]
               [:span.whitespace-nowrap
                (if (= (listen ::subs/locale) :ja)
                  (:nameJp item)
                  (:name item))]]]))]]]])]])
